import { useState, useEffect } from 'react';
import { AlertOctagon, Play } from 'lucide-react';
import { Card, CardBody, CardTitle } from '@/components/ui/Card';
import { Toggle } from '@/components/ui/Toggle';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { useDeviceMutation } from '@/hooks/useDeviceMutation';
import type { DeviceStatus } from '@/lib/types';

interface Props { device: DeviceStatus; gateDisabled: boolean }

export function ModePanel({ device, gateDisabled }: Props) {
  const auto = (device.mode ?? 'auto') === 'auto';
  const flow = !!device.flowDetectionEnabled;

  const [t1, setT1] = useState<number>(device.actTime1 ?? 10);
  const [t2, setT2] = useState<number>(device.actTime2 ?? 20);

  // When the device echoes back updated act times, sync local state.
  useEffect(() => { if (device.actTime1) setT1(device.actTime1); }, [device.actTime1]);
  useEffect(() => { if (device.actTime2) setT2(device.actTime2); }, [device.actTime2]);

  const modeMut = useDeviceMutation<{ mode: 'auto' | 'manual' }>(
    device.client_id, 'mode',
    {
      optimistic: (prev, v) => prev ? ({ ...prev, mode: v.mode }) : prev,
      successToast: (v) => `Mode → ${v.mode}`,
    },
  );

  const flowMut = useDeviceMutation<{ enabled: boolean }>(
    device.client_id, 'flow-detection',
    {
      optimistic: (prev, v) => prev ? ({ ...prev, flowDetectionEnabled: v.enabled }) : prev,
      successToast: (v) => `Flow detection ${v.enabled ? 'on' : 'off'}`,
    },
  );

  const actMut = useDeviceMutation<{ actTime1: number; actTime2: number }>(
    device.client_id, 'act-times',
    { successToast: (v) => `Saved T1=${v.actTime1}s · T2=${v.actTime2}s` },
  );

  const startMut = useDeviceMutation<Record<string, never>>(
    device.client_id, 'start-cycle',
    { successToast: 'Cleaning cycle started' },
  );

  const stopMut = useDeviceMutation<Record<string, never>>(
    device.client_id, 'emergency-stop',
    { successToast: 'EMERGENCY STOP activated' },
  );

  return (
    <Card>
      <CardBody className="space-y-5">
        <div className="flex items-center justify-between">
          <CardTitle>Operating mode</CardTitle>
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
            {auto ? 'Auto' : 'Manual'}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-600">Auto cycle</span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 font-medium">Manual</span>
            <Toggle
              checked={auto}
              disabled={gateDisabled}
              onCheckedChange={(c) => modeMut.mutate({ mode: c ? 'auto' : 'manual' })}
              aria-label="Operating mode"
            />
            <span className="text-xs text-slate-500 font-medium">Auto</span>
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <span className="text-sm text-slate-600">Flow detection</span>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold ${flow ? 'text-emerald-600' : 'text-slate-400'}`}>
              {flow ? 'ON' : 'OFF'}
            </span>
            <Toggle
              checked={flow}
              disabled={gateDisabled}
              onCheckedChange={(c) => flowMut.mutate({ enabled: c })}
              aria-label="Flow detection"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-100">
          <label className="block">
            <span className="text-xs font-semibold text-slate-600 mb-1 block">T1 (sec)</span>
            <Input type="number" min={1} max={3600} value={t1}
              disabled={gateDisabled}
              onChange={(e) => setT1(parseInt(e.target.value || '0', 10))} />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-600 mb-1 block">T2 (sec)</span>
            <Input type="number" min={1} max={3600} value={t2}
              disabled={gateDisabled}
              onChange={(e) => setT2(parseInt(e.target.value || '0', 10))} />
          </label>
        </div>
        <Button
          variant="outline" size="sm" className="w-full"
          disabled={gateDisabled || actMut.isPending || !t1 || !t2}
          loading={actMut.isPending}
          onClick={() => actMut.mutate({ actTime1: t1, actTime2: t2 })}
        >
          Save actuation times
        </Button>

        <div className="pt-3 border-t border-slate-100 grid grid-cols-2 gap-2">
          <Button
            variant="secondary"
            disabled={gateDisabled || startMut.isPending}
            loading={startMut.isPending}
            onClick={() => {
              if (confirm('Start the full cleaning cycle?')) startMut.mutate({});
            }}
          >
            <Play className="w-4 h-4" /> Start cycle
          </Button>
          <Button
            variant="danger"
            disabled={gateDisabled || stopMut.isPending}
            loading={stopMut.isPending}
            onClick={() => {
              if (confirm('EMERGENCY STOP — turn all relays off immediately?')) stopMut.mutate({});
            }}
          >
            <AlertOctagon className="w-4 h-4" /> E-Stop
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
